import React, {
  useState,
  createContext,
  useContext,
  SyntheticEvent,
} from 'react';
import MaterialSnackbar from '@mui/material/Snackbar';
import styled from '../theme';
import { SnackbarContent } from '@mui/material';
import { DriftSnackbar, DriftSnackbarProps } from 'drift-ui';

interface ShowSnackbarProps {
  message: string;
  type: 'info' | 'error';
}

interface SnackbarProps {
  message: string;
  type: 'info' | 'error';
  isOpen: boolean;
}

const SnackbarContext = createContext<any | undefined>(undefined);

const SnackbarStyled = styled(MaterialSnackbar)<{ type: 'info' | 'error' }>`
  .MuiSnackbarContent-root {
    background-color: ${({ theme, type }) =>
      type === 'info' ? theme.colors.highlightOne : theme.colors.error};
  }
  .MuiSnackbar-anchorOriginBottomCenter {
    bottom: 34px;
  }
`;

export const SnackbarProvider = ({ children }: any) => {
  const [snack, setSnackbar] = useState<SnackbarProps>();
  const [driftSnackbar, SetdriftSnackbar] = useState<DriftSnackbarProps>();

  const showSnackbar = (snackbar: ShowSnackbarProps) => {
    setSnackbar({ ...snackbar, isOpen: true });
  };

  const showDriftSnackbar = (driftSnackbar: DriftSnackbarProps) =>
    SetdriftSnackbar({ ...driftSnackbar, open: true });

  const handleClose = (
    _event: Event | SyntheticEvent<any, Event>,
    reason?: 'timeout' | 'clickaway' | 'escapeKeyDown'
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    if (driftSnackbar) {
      SetdriftSnackbar({ ...driftSnackbar, open: false });
    }

    if (snack) {
      setSnackbar({ ...snack, isOpen: false });
    }
  };

  const buttonOnClick = () => {
    SetdriftSnackbar({ ...driftSnackbar, open: false });
  };

  const value = {
    showSnackbar,
    showDriftSnackbar,
    buttonOnClick,
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {snack && (
        <SnackbarStyled
          type={snack?.type}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={snack.isOpen}
          onClose={handleClose}
          autoHideDuration={4000}
        >
          <SnackbarContent message={snack.message}></SnackbarContent>
        </SnackbarStyled>
      )}
      {driftSnackbar && (
        <DriftSnackbar
          autoHideDuration={4000}
          onClose={handleClose}
          {...driftSnackbar}
        />
      )}
    </SnackbarContext.Provider>
  );
};

const useSnackbars = (): {
  showSnackbar: (snackbar: ShowSnackbarProps) => void;
  showDriftSnackbar: (drifSnackbar: DriftSnackbarProps) => void;
  buttonOnClick: () => void;
} => {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbars must be used within the SnackbarProvider');
  }

  return context;
};

export default useSnackbars;
